import React, { useState, useEffect } from 'react';
import axios from 'axios';

const IdeasForm = ({ journeyId, token, userRole, onSuccess, onCancel }) => {
  const [ideas, setIdeas] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedIdeas, setSelectedIdeas] = useState(new Set());

  // Form state for new idea
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: [],
  });

  // Scoring state
  const [scoringIdea, setScoringIdea] = useState(null);
  const [scores, setScores] = useState({
    impact_score: 5,
    feasibility_score: 5,
    novelty_score: 5,
    comment: '',
  });

  // Load ideas on mount
  useEffect(() => {
    loadIdeas();
  }, [journeyId]);

  const loadIdeas = async () => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/journeys/${journeyId}/ideas`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setIdeas(response.data.ideas);
    } catch (err) {
      console.error('Load ideas error:', err);
    }
  };

  // Helper function to render star rating
  const renderStars = (score) => {
    if (!score) return '☆ ☆ ☆ ☆ ☆';
    const stars = [];
    const maxScore = 10;
    for (let i = 1; i <= 5; i++) {
      const threshold = (i / 5) * maxScore;
      stars.push(score >= threshold ? '★' : '☆');
    }
    return stars.join(' ');
  };

  // Helper function to calculate consensus level based on average score
  const calculateConsensus = (idea) => {
    if (!idea.impact_score || !idea.feasibility_score || !idea.novelty_score) {
      return null;
    }
    const scores = [idea.impact_score, idea.feasibility_score, idea.novelty_score];
    const avg = scores.reduce((a, b) => a + b) / scores.length;

    // Based on MVP scoring framework
    if (avg >= 7) return 'HIGH';
    if (avg >= 4) return 'MED';
    return 'LOW';
  };

  // Helper function to get consensus badge color
  const getConsensusColor = (level) => {
    switch (level) {
      case 'HIGH':
        return '#27ae60';
      case 'MED':
        return '#f39c12';
      case 'LOW':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmitIdea = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/ideas`,
        {
          title: formData.title.trim(),
          description: formData.description.trim(),
          tags: formData.tags.length > 0 ? formData.tags : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccessMessage(`Idea "${response.data.idea.title}" submitted!`);
      setFormData({ title: '', description: '', tags: [] });
      await loadIdeas();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to submit idea';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreIdea = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/ideas/${scoringIdea.id}/score`,
        scores,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccessMessage(`Idea scored: ${response.data.scores.overall_score}/10`);
      setScoringIdea(null);
      setScores({ impact_score: 5, feasibility_score: 5, novelty_score: 5, comment: '' });
      await loadIdeas();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to score idea';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (e) => {
    const { name, value } = e.target;
    if (name === 'comment') {
      setScores({ ...scores, [name]: value });
    } else {
      setScores({ ...scores, [name]: parseInt(value) });
    }
  };

  const toggleSelectIdea = async (ideaId, currentStatus) => {
    setLoading(true);
    try {
      const newStatus = !currentStatus;
      await axios.put(
        `http://localhost:5000/api/journeys/${journeyId}/ideas/${ideaId}/select`,
        { is_selected: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local selection state
      const newSelected = new Set(selectedIdeas);
      if (newSelected.has(ideaId)) {
        newSelected.delete(ideaId);
      } else {
        newSelected.add(ideaId);
      }
      setSelectedIdeas(newSelected);

      // Reload ideas to sync with backend
      await loadIdeas();
      setSuccessMessage(newStatus ? '✓ Idea selected' : '✓ Idea deselected');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Selection error:', err);
      setErrorMessage('Failed to update selection');
    } finally {
      setLoading(false);
    }
  };

  if (scoringIdea) {
    return (
      <div className="form-card">
        <h2 className="form-title">Score Idea</h2>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          <strong>{scoringIdea.title}</strong>
        </p>

        {successMessage && <div className="form-success">{successMessage}</div>}
        {errorMessage && <div className="form-error">{errorMessage}</div>}

        <form onSubmit={handleScoreIdea}>
          <div className="form-group">
            <label>Impact Score (1-10)</label>
            <input
              type="range"
              name="impact_score"
              min="1"
              max="10"
              value={scores.impact_score}
              onChange={handleScoreChange}
            />
            <span style={{ marginLeft: '10px' }}>{scores.impact_score}</span>
          </div>

          <div className="form-group">
            <label>Feasibility Score (1-10)</label>
            <input
              type="range"
              name="feasibility_score"
              min="1"
              max="10"
              value={scores.feasibility_score}
              onChange={handleScoreChange}
            />
            <span style={{ marginLeft: '10px' }}>{scores.feasibility_score}</span>
          </div>

          <div className="form-group">
            <label>Novelty Score (1-10)</label>
            <input
              type="range"
              name="novelty_score"
              min="1"
              max="10"
              value={scores.novelty_score}
              onChange={handleScoreChange}
            />
            <span style={{ marginLeft: '10px' }}>{scores.novelty_score}</span>
          </div>

          <div className="form-group">
            <label>Comment (optional)</label>
            <textarea
              name="comment"
              value={scores.comment}
              onChange={handleScoreChange}
              placeholder="Add your feedback..."
              maxLength="500"
            />
            <div className="char-count">{scores.comment.length}/500</div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setScoringIdea(null)}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Scoring...' : 'Submit Score'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      {/* Ideas List */}
      <div className="form-card">
        <h2 className="form-title">💡 Ideas</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>Step 3-5 of 5: Ideation & Scoring</p>

        {successMessage && <div className="form-success">{successMessage}</div>}
        {errorMessage && <div className="form-error">{errorMessage}</div>}

        {ideas.length === 0 ? (
          <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
            No ideas yet. Submit your first idea!
          </p>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            {ideas.sort((a, b) => {
              const scoreA = a.overall_score || 0;
              const scoreB = b.overall_score || 0;
              return scoreB - scoreA;
            }).map((idea) => {
              const consensus = calculateConsensus(idea);
              const consensusColor = getConsensusColor(consensus);

              return (
                <div
                  key={idea.id}
                  style={{
                    padding: '15px',
                    marginBottom: '10px',
                    border: idea.is_selected ? '2px solid #0066cc' : '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: idea.is_selected ? '#f0f6ff' : '#f9f9f9',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                        {idea.title}
                      </h4>
                      <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
                        {idea.description}
                      </p>

                      {idea.overall_score && Number(idea.overall_score) > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '14px' }}>
                          {/* Rating Stars */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '18px', color: '#f39c12' }}>
                              {renderStars(Number(idea.overall_score))}
                            </span>
                            <span style={{ fontWeight: '600', color: '#0066cc' }}>
                              {Number(idea.overall_score).toFixed(1)}/10
                            </span>
                          </div>

                          {/* Score Breakdown */}
                          <div style={{ color: '#7f8c8d', fontSize: '12px' }}>
                            Impact: {idea.impact_score} | Feasibility: {idea.feasibility_score} | Novelty: {idea.novelty_score}
                          </div>

                          {/* Consensus Badge */}
                          {consensus && (
                            <div
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                backgroundColor: consensusColor,
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '600',
                              }}
                            >
                              {consensus} Consensus
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                          Not yet scored
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '140px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => setScoringIdea(idea)}
                        style={{ fontSize: '13px', padding: '8px 12px' }}
                      >
                        Score
                      </button>

                      {userRole === 'facilitator' && (
                        <button
                          className={`btn ${idea.is_selected ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => toggleSelectIdea(idea.id, idea.is_selected)}
                          disabled={loading}
                          style={{
                            fontSize: '13px',
                            padding: '8px 12px',
                            backgroundColor: idea.is_selected ? '#0066cc' : undefined,
                            color: idea.is_selected ? 'white' : undefined,
                          }}
                        >
                          {idea.is_selected ? '✓ Selected' : 'Select'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Toggle Form */}
        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowForm(!showForm)}
            style={{ width: '100%', marginBottom: '10px' }}
          >
            {showForm ? 'Hide Idea Form' : 'Show Idea Form'}
          </button>
        </div>
      </div>

      {/* Idea Submission Form */}
      {showForm && (
        <div className="form-card" style={{ marginTop: '20px' }}>
          <h3>Submit New Idea</h3>

          <form onSubmit={handleSubmitIdea}>
            <div className="form-group">
              <label>Idea Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief title for your idea (5-255 chars)"
                maxLength="255"
                required
              />
              <div className="char-count">{formData.title.length}/255</div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your idea in detail..."
                maxLength="2000"
                required
              />
              <div className="char-count">{formData.description.length}/2000</div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Idea'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Actions */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        {onCancel && (
          <button className="btn btn-secondary" onClick={onCancel}>
            Back
          </button>
        )}
        {onSuccess && ideas.length > 0 && userRole === 'facilitator' && (
          <button
            className="btn btn-primary"
            onClick={() => {
              const selectedIdeasList = ideas.filter(i => i.is_selected);
              onSuccess(selectedIdeasList.length > 0 ? selectedIdeasList : ideas);
            }}
            style={{ marginLeft: '10px' }}
          >
            {ideas.filter(i => i.is_selected).length > 0
              ? `Move Forward with ${ideas.filter(i => i.is_selected).length} Selected`
              : 'Complete Journey'}
          </button>
        )}
      </div>
    </div>
  );
};

export default IdeasForm;
